import React, { useEffect, useRef, useState } from 'react';

// Components
import VerticalNavLink from 'components/navV3/PostAuth/VerticalNavLink';

// Constants
import { PrimaryNavIdentifier } from '@zola-helpers/client/dist/es/constants/navConstants';

// Types
import { UserContext } from '@zola-helpers/client/dist/es/@types';

// Utils
import { trackNavigationClicked } from 'util/trackingHelper';
import TextUtil from 'util/TextUtil';
import { getPrimaryLinks, getSecondaryLinks } from 'components/navV3/PostAuth/verticalNavData';

// Styles
import COLORS from '@zola/zola-ui/src/styles/emotion/colors3';
import { VerticalNavWrapper, HomeLink, StyledZolaLogo, SecondaryLinks } from './VerticalNav.styles';

type VerticalNavProps = {
  activeLinkId: '' | PrimaryNavIdentifier | undefined;
  userContext: UserContext;
};

const VerticalNav = ({ activeLinkId, userContext }: VerticalNavProps): JSX.Element => {
  const [open, setOpen] = useState<boolean | undefined>();
  const openDelayTimeout = useRef<number>();
  const HOVER_DELAY_MS = 300;

  const primaryLinks = getPrimaryLinks(userContext);
  const secondaryLinks = getSecondaryLinks();

  const handleFirstLinkKeyDown = (e: { key: string; shiftKey: boolean }) => {
    if (e.key === 'Tab' && e.shiftKey) {
      setOpen(false);
    }
  };

  const handleOpen = () => {
    openDelayTimeout.current = window.setTimeout(() => setOpen(true), HOVER_DELAY_MS);
    return openDelayTimeout;
  };

  const handleClose = () => {
    clearTimeout(openDelayTimeout.current);
    setOpen(false);
  };

  useEffect(
    () => () => {
      clearTimeout(openDelayTimeout.current);
    },
    [openDelayTimeout]
  );

  const getEventData = (name: string, rowPosition: number) => {
    return {
      menu_item: TextUtil.toAllUpperCase(name),
      section: 'PRIMARY',
      node_level_1: TextUtil.toAllUpperCase(name),
      row_position: rowPosition + 1,
      column_position: 1,
    };
  };

  return (
    <VerticalNavWrapper open={open} onMouseEnter={handleOpen} onMouseLeave={handleClose}>
      <HomeLink
        aria-label="Zola Homepage"
        href="/"
        onKeyDown={handleFirstLinkKeyDown}
        onKeyUp={handleOpen}
        open={open}
        onClick={() => trackNavigationClicked(getEventData('HOME', 0))}
      >
        <StyledZolaLogo color={COLORS.BLACK_100} open={open} type={open ? 'logomark' : 'heart'} />
      </HomeLink>
      {primaryLinks.map((link, i) => {
        const isLastLink = !open && i === primaryLinks.length - 1;
        const eventData = getEventData(link.name, i + 1); // add one to offset the home link

        return (
          <VerticalNavLink
            active={link.activeNavType === activeLinkId}
            handleOpenNav={handleOpen}
            isLastLink={isLastLink}
            key={`vertical-nav-link-${i}`}
            link={link}
            navOpened={open}
            onClick={() => trackNavigationClicked(eventData)}
          />
        );
      })}
      {open ? (
        <SecondaryLinks open={open}>
          {secondaryLinks.map((link, i) => {
            const isLastLink = i === secondaryLinks.length - 1;
            const eventData = getEventData(link.name, i + primaryLinks.length);

            return (
              <VerticalNavLink
                active={link.activeNavType === activeLinkId}
                handleOpenNav={handleOpen}
                handleCloseNav={handleClose}
                isLastLink={isLastLink}
                key={`vertical-nav-link-${i + primaryLinks.length}`}
                link={link}
                navOpened={open}
                type="secondary"
                onClick={() => trackNavigationClicked(eventData)}
              />
            );
          })}
        </SecondaryLinks>
      ) : null}
    </VerticalNavWrapper>
  );
};

export default VerticalNav;
